import React, { useEffect, useState } from 'react'
import BreadCrumbs from '../components/BreadCrumbs'
import TopAddress from '../components/TopAddress'
import StaticPageHeader from '../components/common/StaticPageHeader';
import { callGetWebservice } from '../hooks/api';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import '../assets/css/allpages.css';


const OrderDetailSkeleton = () => {
    return (
        <div className="order-detail-skeleton">
            {/* Order Header */}
            <div className="skeleton-section mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="skeleton-bar w-25"></div>
                    <div className="skeleton-bar w-15"></div>
                </div>
            </div>

            {/* Order Status */}
            <div className="skeleton-section mb-4">
                <div className="row">
                    <div className="col-lg-3 col-md-6">
                        <div className="skeleton-bar w-70 mb-2"></div>
                        <div className="skeleton-bar w-100"></div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="skeleton-bar w-70 mb-2"></div>
                        <div className="skeleton-bar w-100"></div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="skeleton-bar w-70 mb-2"></div>
                        <div className="skeleton-bar w-100"></div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="skeleton-bar w-70 mb-2"></div>
                        <div className="skeleton-bar w-100"></div>
                    </div>
                </div>
            </div>

            {/* Order Items */}
            <div className="skeleton-section mb-4">
                {[1, 2].map((item) => (
                    <div key={item} className="skeleton-item d-flex gap-3 mb-3">
                        <div className="skeleton-image"></div>
                        <div className="flex-grow-1">
                            <div className="skeleton-bar w-50 mb-2"></div>
                            <div className="skeleton-bar w-30 mb-2"></div>
                            <div className="skeleton-bar w-20"></div>
                        </div>
                        <div className="skeleton-price">
                            <div className="skeleton-bar w-100"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const OrderDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { slug } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        callGetWebservice('order-details', {
            slug: slug
        }, (response) => {
            setOrder(response.data);
            setLoading(false);
        }, null, dispatch, navigate);
    }, [slug, dispatch, navigate]);

    if (loading) {
        return (
            <>
                <BreadCrumbs title1={'Home'} title2={'Orders'} title3={'Order Detail'} />
                <section className="middle">
                    <div className="container">
                        <OrderDetailSkeleton />
                    </div>
                </section>
            </>
        );
    }

    if (!order) {
        return <div className="text-center">Loading...</div>;
    }

    const getStatusClass = (status) => {
        if (!status) return 'bg-primary text-white border border-primary';
        const s = status.toString().toLowerCase();
        if (s.includes('pending') || s.includes('new')) return 'bg-info text-white border border-info';
        if (s.includes('processing')) return 'bg-primary text-white border border-primary';
        if (s.includes('completed') || s.includes('delivered')) return 'bg-success text-white border border-success';
        if (s.includes('cancelled')) return 'bg-danger text-white border border-danger';
        if (s.includes('shipped')) return 'bg-warning text-dark border border-warning';
        return 'bg-secondary text-white border border-secondary';
    };

    const paymentText = order.order_transactions?.[0]?.card_number
        ? `Paid via Card (**** **** **** ${order.order_transactions[0].card_number.slice(-4)})`
        : `Cash on Delivery`;

    return (
        <div className="static-page-wrapper bg-light min-vh-100 pb-5">
            <StaticPageHeader title="Order Details" />

            <div className="container static-page-container mt-4">
                <div className="row align-items-start mt-1">
                    <TopAddress />
                    {/* LEFT COLUMN: Main Order Information */}
                    <div className="col-12 col-md-8">
                        <div className="bg-white border shadow-sm p-4 mb-4" style={{ borderRadius: '12px' }}>
                            {/* Order Header */}
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center border-bottom pb-4 mb-4">
                                <div className="d-flex align-items-center flex-wrap mb-3 mb-lg-0">
                                    <div className="mr-3">
                                        <h2 className="fw-bold mb-1 text-dark" style={{ fontSize: '1.75rem' }}>Order #{order.slug}</h2>
                                        <p className="text-muted mb-0 fw-medium">Placed on {order.created_at}</p>
                                    </div>
                                    <div className={`badge fw-semibold px-3 py-2 ${getStatusClass(order.order_status?.name || order.order_status_id)}`} style={{ fontSize: '0.85rem', letterSpacing: '0.5px', borderRadius: '30px' }}>
                                        {(order.order_status?.name || order.order_status_id || 'Processing').toString().toUpperCase()}
                                    </div>
                                </div>
                                
                                <div className="d-flex gap-2">
                                    <button onClick={() => navigate('/track')} className="btn btn-dark fw-medium shadow-sm transition-hover px-4" style={{ borderRadius: '8px' }}>
                                        <i className="fas fa-crosshairs mr-2"></i> Track Order
                                    </button>
                                    {(order.payment_status?.name === 'Paid' || order.payment_status === 'Paid') && (
                                        <button 
                                            onClick={() => {
                                                let apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/';
                                                if (apiUrl.endsWith('/')) apiUrl = apiUrl.slice(0, -1);
                                                const merchantId = order.merchant_id || 1;
                                                window.open(`${apiUrl}/receipt?merchantId=${merchantId}&orderSlug=${order.slug}&format=pdf`, '_blank');
                                            }} 
                                            className="btn btn-outline-dark bg-white fw-medium transition-hover px-4"
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <i className="fas fa-file-invoice mr-2"></i> Invoice
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Payment and Customer Info in the same row */}
                            <div className="row mb-5 border-bottom pb-4">
                                <div className="col-12 col-md-6 mb-4 mb-md-0 border-right-md">
                                    <h6 className="fw-semibold text-dark mb-4">
                                        <i className="fas fa-credit-card mr-2 text-secondary"></i>Payment Information
                                    </h6>
                                    
                                    <div className="mb-3 d-flex justify-content-between align-items-center pr-md-4">
                                        <span className="text-secondary small fw-medium">Method</span>
                                        <span className="text-dark fw-medium text-right">
                                            {(order.payment?.name === 'Manual Payment' || !order.payment?.name) 
                                                ? 'Cash on Delivery' 
                                                : order.payment.name}
                                        </span>
                                    </div>
                                    
                                    <div className="mb-3 d-flex justify-content-between align-items-center pr-md-4">
                                        <span className="text-secondary small fw-medium">Status</span>
                                        <span className={`badge ${
                                            (order.payment_status?.name === 'Paid' || order.payment_status === 'Paid') 
                                            ? 'bg-success text-white border border-success' 
                                            : 'bg-danger text-white border border-danger'
                                        } px-2 py-1`} style={{ borderRadius: '30px' }}>
                                            {(order.payment_status?.name || order.payment_status || 'Pending').toString().toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 pl-md-4">
                                    <h6 className="fw-semibold text-dark mb-4">
                                        <i className="fas fa-map-marker-alt mr-2 text-secondary"></i>Order Summary & Details
                                    </h6>
                                    
                                    <div className="mb-4">
                                        <p className="text-secondary small fw-semibold text-uppercase mb-2" style={{ letterSpacing: '0.5px' }}>
                                            Shipping Address
                                        </p>
                                        <p className="mb-0 fw-medium text-dark">{order.order_details?.[0]?.ship_name || `${order.first_name} ${order.last_name}`}</p>
                                        <p className="mb-0 text-secondary small">{order.order_details?.[0]?.ship_address || order.address_1}</p>
                                        <p className="mb-0 text-secondary small">{order.order_details?.[0]?.ship_city || order.city}, {order.order_details?.[0]?.ship_state || order.state} {order.order_details?.[0]?.ship_zipcode || order.postcode}</p>
                                    </div>

                                    <div className="mb-0">
                                        <p className="text-secondary small fw-semibold text-uppercase mb-2" style={{ letterSpacing: '0.5px' }}>
                                            Billing Address
                                        </p>
                                        <p className="mb-0 fw-medium text-dark">{order.order_details?.[0]?.bill_name || `${order.first_name} ${order.last_name}`}</p>
                                        <p className="mb-0 text-secondary small">{order.order_details?.[0]?.bill_address || order.address_1}</p>
                                        <p className="mb-0 text-secondary small">{order.order_details?.[0]?.bill_city || order.city}, {order.order_details?.[0]?.bill_state || order.state} {order.order_details?.[0]?.bill_zipcode || order.postcode}</p>
                                    </div>
                                </div>
                            </div>


                            {/* Order Items */}
                            <h5 className="fw-semibold mb-4 text-dark border-top pt-4" style={{ fontSize: '1.25rem' }}>Items</h5>
                            <div className="order-items-list">
                                {order.order_items.map((orderItem, itemIndex) => (
                                    <div key={itemIndex} className="d-flex flex-column flex-md-row align-items-start align-items-md-center border-bottom py-4">
                                        <div style={{ width: '70px', height: '70px', flexShrink: 0 }} className="rounded bg-light border mr-4 p-2">
                                            <img
                                                src={orderItem.itemable?.image || 'https://via.placeholder.com/70'}
                                                alt={orderItem.itemable?.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        </div>
                                        
                                        <div className="flex-grow-1 w-100 pr-md-4 mt-3 mt-md-0">
                                            <h6 className="fw-semibold mb-1 text-dark">{orderItem.itemable?.name}</h6>
                                            <div className="text-secondary small">Size: <span className="fw-medium text-dark">S</span></div>
                                            <div className="text-secondary small">Quantity: <span className="fw-medium text-dark">{orderItem.quantity}</span></div>
                                        </div>

                                        <div className="text-md-right mt-3 mt-md-0" style={{ minWidth: '100px' }}>
                                            <span className="fw-semibold text-dark" style={{ fontSize: '1.25rem' }}>{order.currency?.symbol} {(orderItem.sale_price * orderItem.quantity).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Totals Section */}
                            <div className="mt-4 pt-4 d-flex justify-content-end">
                                <div style={{ minWidth: '300px' }}>
                                    <div className="d-flex justify-content-between mb-2 text-secondary">
                                        <span>Subtotal</span>
                                        <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.total_price || order.sub_total || order.total_amount).toFixed(2)}</span>
                                    </div>
                                    
                                    {(order.total_quantity > 0) && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>Total Quantity</span>
                                            <span className="text-dark fw-medium">{order.total_quantity} items</span>
                                        </div>
                                    )}

                                    {parseFloat(order.total_shipment) > 0 && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>Shipping</span>
                                            <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.total_shipment).toFixed(2)}</span>
                                        </div>
                                    )}

                                    {(parseFloat(order.gst_val) > 0 || parseFloat(order.gst) > 0) && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>GST {order.gst_val ? `(${order.gst_val}%)` : (order.gst ? `(${order.gst}%)` : '')}</span>
                                            <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.gst || order.gst).toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    {(parseFloat(order.sales_tax) > 0) && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>Sales Tax {order.sales_tax_per ? `(${order.sales_tax_per}%)` : ''}</span>
                                            <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.sales_tax).toFixed(2)}</span>
                                        </div>
                                    )}

                                    {(parseFloat(order.income_tax) > 0) && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>Income Tax {order.income_tax_per ? `(${order.income_tax_per}%)` : ''}</span>
                                            <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.income_tax).toFixed(2)}</span>
                                        </div>
                                    )}

                                    {(parseFloat(order.tip_amount) > 0 || parseFloat(order.tip) > 0) && (
                                        <div className="d-flex justify-content-between mb-2 text-secondary">
                                            <span>Tip {order.tip_percentage ? `(${order.tip_percentage}%)` : (order.tip_per ? `(${order.tip_per}%)` : '')}</span>
                                            <span className="text-dark fw-medium">{order.currency?.symbol} {parseFloat(order.tip_amount || order.tip).toFixed(2)}</span>
                                        </div>
                                    )}
                                    
                                    <div className="d-flex justify-content-between mt-3 pt-3 border-top">
                                        <span className="fw-semibold text-dark" style={{ fontSize: '1.25rem' }}>Total</span>
                                        <span className="fw-bold text-dark" style={{ fontSize: '1.5rem' }}>{order.currency?.symbol} {parseFloat(order.total_amount).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {order.additional_comments && (
                                <div className="mt-4 pt-3 border-top">
                                    <h6 className="fw-semibold text-dark mb-2">Additional Comments</h6>
                                    <p className="text-secondary small mb-0 p-3 bg-light rounded">{order.additional_comments}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetail;